import { Button } from "@dash/form";
import { useProfiles } from "@megarax/features";
import { useTheme } from "@emotion/react";
import React, { useContext, useState } from "react";
import { MdOutlineMailOutline } from "react-icons/md";
import { useQueryClient } from "react-query";

import { IamAuthContext } from "@megaron/auth-react";
import { useClientManager } from "@megaron/react-clients";
import { TicketDetailDto } from "@megaron/support-contracts";

type Props = { ticket: TicketDetailDto };

export const SupportTicketReplyComposer: React.FC<Props> = ({ ticket }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const support = useClientManager("support");
  const auth = useContext(IamAuthContext);
  const { profile } = useProfiles();
  const [replyBody, setReplyBody] = useState("");
  const userEmail = auth.iamUser?.userType === "megarax" ? auth.iamUser.email : undefined;
  const senderProfile = profile(userEmail ?? null);

  const sendMutation = support.emailSend().useMutation({
    onSuccess: () => {
      setReplyBody("");
      queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] });
    },
  });

  const handleSend = () => {
    const trimmed = replyBody.trim();
    if (!trimmed) return;
    sendMutation.mutate({
      to: ticket.reporterEmail,
      subject: ticket.subject.startsWith("Re:") ? ticket.subject : `Re: ${ticket.subject}`,
      body: trimmed,
      threadId: ticket.threadId ?? undefined,
      senderFirstName: senderProfile?.firstName ?? null,
      senderLastName: senderProfile?.lastName ?? null,
    });
  };

  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <div
        css={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "13px",
          color: theme.colors.secondaryText,
        }}
      >
        <MdOutlineMailOutline size={16} />
        <span>
          Odpowiedź od: <strong>{ticket.sourceMailbox ?? "skrzynka support"}</strong> → {ticket.reporterEmail}
        </span>
      </div>
      <textarea
        value={replyBody}
        onChange={(e) => setReplyBody(e.target.value)}
        placeholder="Napisz odpowiedź do klienta..."
        rows={5}
        css={{
          width: "100%",
          resize: "vertical",
          padding: "10px 12px",
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.smallBorderRadius,
          fontSize: "14px",
          fontFamily: "inherit",
          outline: "none",
          boxSizing: "border-box",
          "&:focus": { borderColor: theme.colors.primary },
        }}
      />
      <div css={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "12px", flexWrap: "wrap" }}>
        {sendMutation.isError && (
          <span css={{ fontSize: "13px", color: theme.colors.danger }}>
            Nie udało się wysłać wiadomości. Sprawdź ustawienia SMTP.
          </span>
        )}
        <Button
          icon={<MdOutlineMailOutline />}
          css={{ borderRadius: "50px", width: "auto", whiteSpace: "nowrap" }}
          onClick={handleSend}
          isDisabled={!replyBody.trim()}
          isLoading={"isLoading" in sendMutation ? sendMutation.isLoading : false}
        >
          Wyślij odpowiedź emailem
        </Button>
      </div>
    </div>
  );
};
